import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { authenticatedFetch } from "@/libs/api";
import {
  Card,
  Tag,
  Space,
  Button,
  Typography,
  Row,
  Col,
  Descriptions,
  Select,
  Input,
  Upload,
  List,
  Avatar,
  Divider,
  Image,
  message,
  Spin,
  theme,
} from "antd";
import {
  ArrowLeftOutlined,
  BugOutlined,
  UserOutlined,
  PaperClipOutlined,
  SendOutlined,
  UploadOutlined,
  DownloadOutlined,
  FileImageOutlined,
  FileOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import PageLayout from "@components/pageLayout/PageLayout.react";
import dayjs from "dayjs";

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const STATUS_CONFIG = {
  open: { color: "error", icon: <ExclamationCircleOutlined />, label: "Open" },
  in_progress: { color: "processing", icon: <ClockCircleOutlined />, label: "In Progress" },
  resolved: { color: "success", icon: <CheckCircleOutlined />, label: "Resolved" },
  closed: { color: "default", icon: <CloseCircleOutlined />, label: "Closed" },
};

const PRIORITY_COLORS = {
  low: "blue",
  medium: "orange",
  high: "red",
  critical: "magenta",
};

const isImageFile = (mimeType) => mimeType && mimeType.startsWith("image/");

export default function BugReportDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = theme.useToken();

  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [commentFiles, setCommentFiles] = useState([]);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const isSuperadmin = localStorage.getItem("roleType") === "SUPERADMIN";

  const fetchReport = useCallback(async () => {
    try {
      const res = await authenticatedFetch(`/api/bug-reports/${id}`);
      const result = await res.json();
      if (result.success) {
        setReport(result.data);
      } else {
        message.error(result.message || "Bug report not found");
        navigate("/bug-reports");
      }
    } catch (err) {
      message.error("Failed to load bug report");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleStatusChange = async (newStatus) => {
    setUpdatingStatus(true);
    try {
      const res = await authenticatedFetch(`/api/bug-reports/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status: newStatus }),
      });
      const result = await res.json();
      if (result.success) {
        message.success("Status updated");
        fetchReport();
      } else {
        message.error(result.message);
      }
    } catch (err) {
      message.error("Failed to update status");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleAddComment = async () => {
    if (!commentText.trim()) {
      message.warning("Please enter a comment");
      return;
    }
    setSubmittingComment(true);
    try {
      const formData = new FormData();
      formData.append("content", commentText);
      commentFiles.forEach((file) => {
        formData.append("files", file.originFileObj || file);
      });

      const res = await authenticatedFetch(`/api/bug-reports/${id}/comments`, {
        method: "POST",
        body: formData,
      });
      const result = await res.json();
      if (result.success) {
        message.success("Comment added");
        setCommentText("");
        setCommentFiles([]);
        fetchReport();
      } else {
        message.error(result.message);
      }
    } catch (err) {
      message.error("Failed to add comment");
    } finally {
      setSubmittingComment(false);
    }
  };

  const getAttachmentsForComment = (commentId) => {
    if (!report?.attachments) return [];
    return report.attachments.filter((a) => a.comment_id === commentId);
  };

  const getReportAttachments = () => {
    if (!report?.attachments) return [];
    return report.attachments.filter((a) => !a.comment_id);
  };

  const renderAttachment = (att) => {
    const isImage = isImageFile(att.mime_type);
    const downloadUrl = `/api/bug-reports/attachment/${att.id}/download`;
    const authToken = localStorage.getItem("token");

    if (isImage) {
      return (
        <div key={att.id} style={{ display: "inline-block", marginRight: 8, marginBottom: 8 }}>
          <Image
            src={`${downloadUrl}?token=${authToken}`}
            alt={att.file_name}
            width={120}
            height={90}
            style={{ objectFit: "cover", borderRadius: 4, border: "1px solid #d9d9d9" }}
            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=="
          />
          <div style={{ fontSize: 11, maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {att.file_name}
          </div>
        </div>
      );
    }

    return (
      <Button
        key={att.id}
        size="small"
        icon={<DownloadOutlined />}
        href={downloadUrl}
        target="_blank"
        style={{ marginRight: 8, marginBottom: 4 }}
      >
        {att.file_name} ({(att.file_size / 1024).toFixed(1)}KB)
      </Button>
    );
  };

  if (loading) {
    return (
      <PageLayout active="bugReports">
        <div style={{ textAlign: "center", padding: 100 }}>
          <Spin size="large" />
        </div>
      </PageLayout>
    );
  }

  if (!report) return null;

  const statusCfg = STATUS_CONFIG[report.status] || STATUS_CONFIG.open;
  const reportAttachments = getReportAttachments();

  return (
    <PageLayout active="bugReports">
      <div style={{ maxWidth: 1000, margin: "0 auto" }}>
        {/* Header */}
        <Row align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate("/bug-reports")}
              style={{ marginRight: 8 }}
            />
          </Col>
          <Col flex="auto">
            <Title level={3} style={{ margin: 0 }}>
              <BugOutlined style={{ marginRight: 8 }} />
              {report.title}
            </Title>
          </Col>
          <Col>
            {isSuperadmin && (
              <Select
                value={report.status}
                onChange={handleStatusChange}
                loading={updatingStatus}
                style={{ width: 150 }}
                options={[
                  { value: "open", label: "Open" },
                  { value: "in_progress", label: "In Progress" },
                  { value: "resolved", label: "Resolved" },
                  { value: "closed", label: "Closed" },
                ]}
              />
            )}
          </Col>
        </Row>

        {/* Report Info */}
        <Card style={{ marginBottom: 16 }}>
          <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small">
            <Descriptions.Item label="Status">
              <Tag color={statusCfg.color} icon={statusCfg.icon}>{statusCfg.label}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Priority">
              <Tag color={PRIORITY_COLORS[report.priority]}>{(report.priority || "medium").toUpperCase()}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Category">
              {report.category ? <Tag>{report.category}</Tag> : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Reporter">
              <Space>
                <Avatar size="small" icon={<UserOutlined />} />
                {report.reporter_name || "Unknown"}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Created">
              {dayjs(report.created_at).format("YYYY-MM-DD HH:mm")}
            </Descriptions.Item>
            <Descriptions.Item label="Updated">
              {dayjs(report.updated_at).format("YYYY-MM-DD HH:mm")}
            </Descriptions.Item>
            {report.page_url && (
              <Descriptions.Item label="Page URL" span={3}>
                <Text code>{report.page_url}</Text>
              </Descriptions.Item>
            )}
            {report.assignee_name && (
              <Descriptions.Item label="Assignee">
                {report.assignee_name}
              </Descriptions.Item>
            )}
          </Descriptions>

          <Divider style={{ margin: "16px 0" }} />

          <Title level={5}>Description</Title>
          <Paragraph style={{ whiteSpace: "pre-wrap" }}>{report.description}</Paragraph>

          {reportAttachments.length > 0 && (
            <>
              <Title level={5}>
                <PaperClipOutlined style={{ marginRight: 4 }} />
                Attachments ({reportAttachments.length})
              </Title>
              <div>{reportAttachments.map(renderAttachment)}</div>
            </>
          )}
        </Card>

        {/* Comments */}
        <Card
          title={`Comments (${report.comments?.length || 0})`}
          style={{ marginBottom: 16 }}
        >
          {report.comments && report.comments.length > 0 ? (
            <List
              dataSource={report.comments}
              renderItem={(comment) => {
                const commentAttachments = getAttachmentsForComment(comment.id);
                return (
                  <List.Item style={{ alignItems: "flex-start" }}>
                    <List.Item.Meta
                      avatar={<Avatar icon={<UserOutlined />} />}
                      title={
                        <Space>
                          <Text strong>{comment.author_name || "Unknown"}</Text>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {dayjs(comment.created_at).format("YYYY-MM-DD HH:mm")}
                          </Text>
                          {comment.status_change && (
                            <Tag color="blue" style={{ fontSize: 11 }}>{comment.status_change}</Tag>
                          )}
                        </Space>
                      }
                      description={
                        <>
                          <Paragraph style={{ whiteSpace: "pre-wrap", marginBottom: commentAttachments.length > 0 ? 8 : 0 }}>
                            {comment.content}
                          </Paragraph>
                          {commentAttachments.length > 0 && (
                            <div>{commentAttachments.map(renderAttachment)}</div>
                          )}
                        </>
                      }
                    />
                  </List.Item>
                );
              }}
            />
          ) : (
            <Text type="secondary">No comments yet</Text>
          )}

          <Divider />

          {/* Add Comment */}
          <div>
            <TextArea
              rows={3}
              placeholder="Add a comment..."
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              maxLength={5000}
            />
            <Row justify="space-between" align="middle" style={{ marginTop: 8 }}>
              <Col>
                <Upload
                  multiple
                  fileList={commentFiles}
                  beforeUpload={() => false}
                  onChange={({ fileList }) => setCommentFiles(fileList)}
                  accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.txt,.log,.zip"
                >
                  <Button icon={<UploadOutlined />} size="small">
                    Attach Files
                  </Button>
                </Upload>
              </Col>
              <Col>
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={handleAddComment}
                  loading={submittingComment}
                  disabled={!commentText.trim()}
                >
                  Comment
                </Button>
              </Col>
            </Row>
          </div>
        </Card>
      </div>
    </PageLayout>
  );
}
